import "@fontsource-variable/inter";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "./styles/index.css";

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiError, errorText } from "./api/client";
import { t } from "./i18n";
import { follow } from "./lib/jump";
import { routeTree } from "./routeTree.gen";
import { useUi } from "./state/ui";

// Nearly every screen shows Vietnamese names (Tấn, Quân): load that subset of the font at
// once, not when the first such name is laid out. Otherwise WebKit holds a page's first frame
// for about a second while it fetches it (R2-A7).
if (typeof document !== "undefined" && "fonts" in document) {
  void document.fonts.load('400 1em "Inter Variable"', "ấ").catch(() => undefined);
}

// A session that ends mid-use (logout elsewhere, deactivation, expiry) sends the person back
// to sign-in instead of leaving broken screens.
function onApiError(error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  const path = window.location.pathname;
  if (path.startsWith("/signin") || path.startsWith("/auth/")) return;
  queryClient.clear();
  // The whole place, its thread and message too (R10-N8).
  const next = path + window.location.search;
  void router.navigate({ to: "/signin", search: { next }, ignoreBlocker: true });
}

// R10-S4: no action fails silently. A failed change that its screen says nothing about gets
// one toast; one whose screen said so already (its own error toast within a moment, or
// `meta.quiet` for an error shown in place) gets nothing more.
function onMutationError(error: unknown, mutation: { meta?: Record<string, unknown> }) {
  onApiError(error);
  if (error instanceof ApiError && error.status === 401) return;
  if (mutation.meta?.quiet) return;
  const last = () => Math.max(0, ...useUi.getState().toasts.map((t) => t.id));
  const before = last();
  setTimeout(() => {
    const said = useUi.getState().toasts.some((t) => t.error && t.id > before);
    if (!said) useUi.getState().toast(errorText(error, t("common.didntGoThrough")), true);
  }, 400);
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => onMutationError(error, mutation),
  }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      // A refusal (4xx) won't change by asking again; a server error or no answer at all
      // (status 0, offline) may.
      retry: (count, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
  },
});
// The admin's lists have no live events: back on the tab (or on the page), they ask again at
// once rather than show up to 15 s of someone else's edits as current (R10-F10).
for (const prefix of [
  "people",
  "teams",
  "team",
  "resources",
  "settings",
  "memory",
  "feedback",
  "audit",
])
  queryClient.setQueryDefaults([prefix], { staleTime: 0 });

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// A tapped push notification (web/public/sw.js) asks the open app to show its page, in place.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
    const data = e.data as { type?: unknown; url?: unknown } | null;
    if (data?.type !== "vx-navigate" || typeof data.url !== "string") return;
    if (!data.url.startsWith("/") || data.url.startsWith("//")) return;
    // A push opens its item, also on a phone that was showing the list there (R10-N9), and
    // again when it's the page already open (R10-B1).
    useUi.getState().setPhone(data.url, "main");
    void follow(router, data.url);
  });
  navigator.serviceWorker.startMessages();
}

const root = document.getElementById("root");
if (root) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
